import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { ResizeImageType } from '@/types';
import { getResizeImage } from '@/utils';
import IfSkeleton from '@/components/IfSkeleton';
import styled, { css } from 'styled-components';
import { mergeRefs } from 'react-merge-refs';

export interface LoadImagePropsType
  extends React.DetailedHTMLProps<
      React.ImgHTMLAttributes<HTMLImageElement>,
      HTMLImageElement
    >,
    Omit<ResizeImageType, 'url'> {
  className?: string;
  imgFit?: React.CSSProperties['objectFit'];
  handleChangeMeta?(args: { width: number; height: number }): void;
  skeletonSize?: { width?: string; height?: string };
  borderRadius?: React.CSSProperties['borderRadius'];
}

/**
 * 이미지가 로드가 완료된 이후에 노출
 * @author 이현우
 */
const LoadImage = React.forwardRef<HTMLImageElement, LoadImagePropsType>(
  function LoadImage(
    {
      src,
      w,
      h,
      q,
      className,
      imgFit,
      skeletonSize: _skeletonSize,
      borderRadius = 0,
      ...props
    }: LoadImagePropsType,
    ref,
  ) {
    const imageRef = useRef<HTMLImageElement>(null);
    const mergeRef = mergeRefs([ref, imageRef]);

    const [imageLoaded, setImageLoaded] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);

    const handleChangeMeta = useCallback(() => {
      if (
        imageRef?.current?.naturalWidth != null &&
        imageRef?.current?.naturalHeight != null
      )
        props?.handleChangeMeta?.({
          width: imageRef?.current?.naturalWidth,
          height: imageRef?.current?.naturalHeight,
        });
    }, [props]);

    const updateLoadedState = useCallback(() => {
      if (src) {
        setImageLoaded(true);
        handleChangeMeta();
      }
    }, [handleChangeMeta, src]);

    const handleImageLoaded: React.ReactEventHandler<HTMLImageElement> =
      useCallback(
        (e) => {
          updateLoadedState();
          props?.onLoad?.(e);
        },
        [props, updateLoadedState],
      );

    const handleImageError: React.ReactEventHandler<HTMLImageElement> =
      useCallback(
        (e) => {
          setHasError(true);
          props?.onError?.(e);
        },
        [props],
      );

    const alreadyLoadedImageUpdateState = useCallback(async () => {
      if (!imageLoaded && imageRef?.current?.complete) {
        updateLoadedState();
        props?.onLoad?.({
          status: 'alreadyLoaded',
        } as any);
      }
    }, [imageLoaded, props, updateLoadedState]);

    const skeletonSize = useMemo(
      () => ({
        width: _skeletonSize?.width || '100%',
        height: _skeletonSize?.height || '100%',
      }),
      [_skeletonSize?.height, _skeletonSize?.width],
    );

    useEffect(() => {
      alreadyLoadedImageUpdateState();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <>
        <IfSkeleton
          isSkeleton={!imageLoaded}
          duration={hasError ? 0 : undefined}
          style={{
            ...skeletonSize,
          }}
          containerStyle={{
            ...skeletonSize,
          }}
          borderRadius={borderRadius}
        />
        <StyledImage
          {...props}
          src={getResizeImage({
            url: src || '',
            w,
            h,
            q,
          })}
          onLoad={handleImageLoaded}
          onError={handleImageError}
          ref={mergeRef}
          imageLoaded={imageLoaded}
          className={className}
          imgFit={imgFit}
          borderRadius={borderRadius}
        />
      </>
    );
  },
);

export default LoadImage;

const StyledImage = styled.img<{
  imageLoaded?: boolean;
  imgFit?: React.CSSProperties['objectFit'];
  borderRadius?: React.CSSProperties['borderRadius'];
}>`
  ${({ imageLoaded, imgFit, borderRadius }) => css`
    translate: opacity 0.3s;
    border-radius: ${borderRadius};
    ${imageLoaded
      ? css`
          width: 100%;
          height: 100%;
          object-fit: ${imgFit};
          opacity: 1;
        `
      : css`
          width: 0;
          height: 0;
          opacity: 0;
        `}
  `}
`;
